import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, Star, Trophy, Flame, Users, Target } from 'lucide-react';
import Button from '../../../components/ui/Button';

const DailyChallenge = ({ challenge, onStart }) => {
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    const updateTimeRemaining = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow?.setDate(tomorrow?.getDate() + 1);
      tomorrow?.setHours(0, 0, 0, 0);
      
      const diff = tomorrow - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      setTimeRemaining(`${hours}h ${minutes}m`);
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden"
    >
      <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 rounded-2xl p-8 text-white relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-4 left-4 w-16 h-16 border-2 border-white rounded-full"></div>
          <div className="absolute top-12 right-8 w-8 h-8 border-2 border-white rounded-full"></div>
          <div className="absolute bottom-8 left-12 w-12 h-12 border-2 border-white rounded-full"></div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 rounded-full p-3">
                <Calendar className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Challenge of the Day</h2>
                <p className="text-white/80">
                  {new Date()?.toLocaleDateString('en-US', { 
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>

            <div className="text-center">
              <div className="flex items-center space-x-2 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm">Resets in</span>
              </div>
              <div className="text-lg font-bold">{timeRemaining}</div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div className="md:col-span-2">
              <h3 className="text-xl font-bold mb-3 flex items-center space-x-2">
                {challenge?.icon && React.cloneElement(challenge?.icon, { className: "w-6 h-6" })}
                <span>{challenge?.title}</span>
              </h3>
              <p className="text-white/90 mb-4">{challenge?.description}</p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                  {challenge?.category}
                </span>
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                  {challenge?.difficulty}
                </span>
              </div>

              <div className="bg-white/10 rounded-lg p-3 mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Star className="w-4 h-4 text-yellow-300" />
                  <span className="font-medium">Special Reward</span>
                </div>
                <p className="text-sm text-white/90">{challenge?.specialReward}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white/10 rounded-lg p-4 text-center">
                <Trophy className="w-8 h-8 mx-auto mb-2 text-yellow-300" />
                <div className="text-2xl font-bold">{challenge?.points}</div>
                <div className="text-sm text-white/80">Points</div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 text-center">
                <Clock className="w-8 h-8 mx-auto mb-2 text-blue-300" />
                <div className="text-2xl font-bold">{challenge?.timeLimit}</div>
                <div className="text-sm text-white/80">Minutes</div>
              </div>

              <div className="bg-white/10 rounded-lg p-4 text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-green-300" />
                <div className="text-2xl font-bold">{challenge?.participants?.toLocaleString()}</div>
                <div className="text-sm text-white/80">Joined</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <Button
              size="lg"
              onClick={() => onStart?.(challenge?.id)}
              className="bg-white text-orange-600 hover:bg-white/90 font-semibold px-8"
            >
              <Flame className="w-5 h-5 mr-2" />
              Accept Daily Challenge
            </Button>
            
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1">
                <Target className="w-4 h-4" />
                <span>No prerequisites</span>
              </div>
              <div className="flex items-center space-x-1">
                <Star className="w-4 h-4 text-yellow-300" />
                <span>Extra rewards</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DailyChallenge;